import React from 'react'

import { Button } from '@material-ui/core'
import { useFormik } from 'formik'
import { useContext, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as Yup from 'yup'

import { mapData } from '@/product/common/components'
import PrimaryButton from '@/product/components/atoms/PrimaryButton'
import GoogleMap from '../components/molecules/GoogleMap/GoogleMap'
import CustomDialog from '../components/organisms/CustomDialog'

import { useLocationContext } from '@/custom/context/location/locationContext'
import AuthContext from '@/product/context/auth/authContext'
import { handleRedirectInternal } from '@/custom/common/components'

const LOCATION_RADIUS_OPTIONS = [
    { value: 1500, show: 'All' },
    { value: 10, show: '10 miles' },
    { value: 20, show: '20 miles' },
    { value: 30, show: '30 miles' },
    { value: 40, show: '40 miles' },
    { value: 50, show: '50 miles' },
    { value: 60, show: '60 miles' },
    { value: 70, show: '70 miles' },
    { value: 80, show: '80 miles' },
    { value: 90, show: '90 miles' },
    { value: 100, show: '100 miles' },
    { value: 110, show: '110 miles' },
    { value: 120, show: '120 miles' },
    { value: 130, show: '130 miles' },
    { value: 140, show: '140 miles' },
    { value: 150, show: '150 miles' },
    { value: 160, show: '160 miles' },
    { value: 170, show: '170 miles' },
    { value: 180, show: '180 miles' },
    { value: 190, show: '190 miles' },
    { value: 200, show: '200 miles' },
]

const LocationSelector = () => {
    const history = useHistory()
    const location = useLocation()
    const { user } = useContext(AuthContext)

    // ✅ UI state (correct place)
    const [openDialog, setOpenDialog] = useState(false)

    const { locationState, saveLocation, DEFAULT_LOCATION, isDefaultLocation } =
        useLocationContext()

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: locationState,
        validationSchema: Yup.object({
            address1: Yup.string().required('Required'),
        }),
        onSubmit: async (values) => {
            await saveLocation(values, (params) => {
                if (location.pathname !== '/search') {
                    handleRedirectInternal(history, `?${params}`)
                } else {
                    handleRedirectInternal(history, `search?${params}`)
                }
            })
            setOpenDialog(false)
        },
    })

    const getLocationLabel = (loc) => {
        if (!loc) return 'Select Location'

        if (loc.city) {
            return [loc.city, loc.state].filter(Boolean).join(', ')
        }

        return loc.address1 || 'Select Location'
    }

    const locationLabel = getLocationLabel(locationState)

    const handleRadiusChange = (event) => {
        const newRadius = parseInt(event.target.value, 10)
        formik.setFieldValue('radius', newRadius)
    }

    const locationSelectorFields = {
        formik: formik,
        data: [
            {
                label: (
                    <span>
                        <span className="material-icons locationChange">location_on</span>Location
                    </span>
                ),
                placeholder: 'Enter the Location',
                type: 'google_place_autocomplete',
                defaultCountry: 'US',
                class: 'col-12',
                name: 'address1',
                placeID: true,
                fullAddress: true,
                stateCityFill: true,
                saveFullAddress: true,
            },
            {
                label: 'Radius (miles)',
                placeholder: 'Select the Radius',
                type: 'select',
                class: 'col-12',
                name: 'radius',
                native: true,
                options: LOCATION_RADIUS_OPTIONS,
                isNoDefaultOptionValue: true,
                onChange: handleRadiusChange,
            },
        ],
    }

    const resetMiles = () => {
        formik.setValues({ ...DEFAULT_LOCATION })
    }

    return (
        <>
            <Button className="text-white" onClick={() => setOpenDialog(true)}>
                {locationLabel}
                <br />
                <span className="material-icons-outlined expand">expand_more</span>
            </Button>

            <CustomDialog
                open={openDialog}
                className="askQuesModal"
                function={() => setOpenDialog(false)}
                title={<div className="askingQ">Select location</div>}
            >
                <p className="paraDes">
                    Search your location and select the maximum preferred pickup distance to see
                    nearby listings.
                </p>
                <div className="row">{mapData(locationSelectorFields)}</div>
                <a onClick={() => resetMiles()} target="_blank" rel="noreferrer">
                    <span className="terms">Reset Miles</span>
                </a>

                <GoogleMap
                    latitude={formik.values.latitude}
                    longitude={formik.values.longitude}
                    locationName={formik.values.address1}
                    radius={formik.values.radius}
                    zoomLevel={11}
                    fullscreenControl={false}
                    width="100%"
                    height="300px"
                    disableControl
                />

                <PrimaryButton
                    id="apply-map"
                    variant="contained"
                    fullWidth
                    btnSize="askQuesBtn mt-3"
                    onClick={formik.handleSubmit}
                >
                    Apply
                </PrimaryButton>
            </CustomDialog>
        </>
    )
}

export default LocationSelector
